import React, { useState, useEffect } from 'react';
import styled, { StylesProps } from '../../../../../lib/styles';
import Spacer from '../../../../../components/presentational/Spacer';
import Button from '../../../../../components/presentational/Button';
import Icon from '../../../../../components/presentational/Icon';

import membershipBlockStyles from './MembershipBlock_styles';
import Typography from '../../../../../components/presentational/Typography';
import { Member, MemberLevelSubscription } from '../../../../../models/user';
import { readTrackingCookie, signOut, } from '../../../../../lib/accountUtils';
import { mapMemberLevelSubscriptions } from '../../../../../lib/accountUtils';
import { months, constructNumberRange } from '../../../../../lib/dateUtilities';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import MembershipSignupPanel from '../../../../../components/presentational/MemberCentre/MembershipSignupPanel';
import MembershipPanel from '../../../../../components/presentational/MemberCentre/MembershipPanel';
import { getMemberProfile, saveMemberProfile } from '../../../../../lib/api/profile';
import Spinner from '../../../../../components/presentational/Spinner';
import { compose } from 'recompose';
import dayjs from 'dayjs';
import clsx from 'clsx';
import _ from 'lodash';
import { MEMBERCENTRE_UNSUBSCRIBE_CLICK, MEMBERCENTRE_UPDATE_CLICK, raiseEvent } from '../../../../../lib/gtm';
import { trackingDataSelector } from '../../../../../store/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { TrackingDataState } from '../../../../../store/reducers/trackingData';
import { updateTrackingData } from '../../../../../store/actions';
dayjs.extend(customParseFormat);

export interface MembershipBlockData {
  type: 'membership_block';
  joinBackgroundImage: string;
  joinPersonImage: string;
  joinTitle: string;
  joinDescription: string;
  joinEmailTextboxPlaceholder: string;
  joinButtonText: string;
  joinConfirmationText: string;

  detailsMaleAvatarImage: string;
  detailsFemaleAvatarImage: string;
  detailsUndefinedGenderAvatarImage: string;
  detailsWelcomeText: string;
  detailsTitle: string;
  detailsManageSubscriptionsButtonText: string;
  detailsManageProfileButtonText: string;
  detailsUnsubscribedMemberWelcomeText: string;

  profileTitle: string;
  profileDescription: string;

  subscriptionsTitle: string;
  subscriptionsDescription: string;

  saveButtonText: string;
  saveSuccessConfirmationText: string;
  unsubscribeButtonText: string;
  resubscribeButtonText: string;
  unsubscribePromptText: string;
  unsubscribeConfirmationText: string;

  impersonating: string;
  cloak: boolean;
  domain: string;
}

export interface MembershipBlockProps {
  block: MembershipBlockData;
  blockIndex: number;
}

export interface MembershipValidation {
  isValid: boolean | undefined;
  message: string;
}

/**
 * Set up the MembershipBlock component. This component can handle a user's membership or can be used to impersonate a user by
 * passing the `email` and `cloak` properties querystring properties. `email` and `cloak` will disregard the current user's
 * membership and will instead use the provided email address to fetch the user's membership information. Impersonation does not
 * write member data to localStorage or the Redux store.
 * Membership can have 2 states:
 * 1. Not a member - shows the signup panel
 * 2. Is a member - shows the membership panel with profile and subscription management
 * @param param0 MembershipBlockProps & StylesProps<typeof membershipBlockStyles>
 * @returns 
 */
export const MembershipBlock = ({
  block,
  styles,
}: MembershipBlockProps & StylesProps<typeof membershipBlockStyles>) => {
  const trackingSelectorData: TrackingDataState = useSelector(trackingDataSelector);
  const { domain } = block;
  
  const dispatch = useDispatch();

  const [isMember, setIsMember] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInformation, setUserInformation] = useState<Member>({ subscriptions: [], dotDigital: {},});
  const [impersonation, setImpersonation] = useState<{email: string, cloak: boolean}>({ email: '', cloak: false, });
  const [redirectData, setRedirectData] = useState<{ email: string, cloak: boolean } | null>(null);
  const [validation, setValidation] = useState<MembershipValidation>({ isValid: true, message: '' });
  const [searchParamItems, setSearchParamItems] = useState<{ email: string, cloak: boolean, updated?: boolean }>({ email: '', cloak: false });

  const [isSaving, setIsSaving] = useState(false);
  const [memberEmail, setMemberEmail] = useState<any>('');
  const [firstName, setFirstName] = useState<any>('');
  const [lastName, setLastName] = useState<any>('');
  const [dob, setDOB] = useState<any>('');
  const [dobDay, setDOBDay] = useState<string>('14');
  const [dobMonth, setDOBMonth] = useState<string>('');
  const [dobYear, setDOBYear] = useState<string>('1966');
  const [postcode, setPostcode] = useState<any>('');
  const [gender, setGender] = useState<any>('');
  const [allSubscriptions, setAllSubscriptions] = useState<MemberLevelSubscription[]>([]);
  const [unsubscribeDate, setUnsubscribeDate] = useState<any>('');
  const [dotDigital, setDotDigital] = useState<any>('');

  const [subscriptionCheckedState, setSubscriptionCheckedState] = useState<boolean[]>(new Array(allSubscriptions.length).fill(false));
  const [promptUnsubscribe, setPromptUnsubscribe] = useState(false);
  const [promptUnsubscribeSuccessful, setPromptUnsubscribeSuccessful] = useState<boolean | undefined>(undefined);
  const [promptConfirmEmail, setPromptConfirmEmail] = useState('');

  //  DEBUGGING
  // useEffect(() => {
  //   console.log('[Debugging] MembershipBlock state', { block });
  // }, [trackingSelectorData]);

  useEffect(() => {
    if (redirectData) {
      // This code will only run after the component has rendered with the new state.
      const { email, cloak } = redirectData;
      setIsSaving(false);
      console.log('[Debugging] MembershipBlock useEffect - redirecting to member centre');
      window.location.href = `/membercentre?updated=true&email=${email}` + (cloak ? '&cloak=true' : '');
    }
  }, [redirectData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paramsFromUrl = {
      email: searchParams.get('email') || '',
      cloak: searchParams.get('cloak') === 'true',
      ...(searchParams.get('updated') && { updated: searchParams.get('updated') === 'true' }),
    };

    setSearchParamItems(paramsFromUrl);
    setImpersonation(paramsFromUrl);

    // console.log('[INIT] MembershipBlock', { 
    //   block, 
    //   paramsFromUrl, 
    //   searchParams, 
    //   updating: paramsFromUrl.updated, 
    //   impersonation,
    //   trackingSelectorData,
    //   validation,
    // });
  }, []);

  //  fetch user information when impersonation is set or tracking data is loaded
  useEffect(() => {
    setIsLoading(true);
    //  this is bound to changes of the impersonation object
    if (!trackingSelectorData.isLoaded) return; // Do not run until impersonation is initialised
    if (!impersonation.email && !trackingSelectorData.email) {
      // Not a member and no email provided - nothing more to do and show signup panel
      console.log('[Debugging] Turning off loading', { trackingSelectorData, impersonation });
      setIsLoading(false);
      return;
    };

    const cookieTrackingData = readTrackingCookie();
    // console.log('[Debugging] MembershipBlock useEffect fetching member information', { impersonation, cookieTrackingData, trackingSelectorData });
    const memberEmail = impersonation.email || cookieTrackingData?.email || trackingSelectorData?.email;
    getMemberProfile(memberEmail!)
      .then(profileData => {
        setUserInformation(prevState => {
          return {
            ...prevState,
            ...profileData,
          };
        });
        // console.log('[Debugging] MembershipBlock getMemberInformation', { profileData, impersonation });
        return profileData;
      })
      .then((profileData) => {
        // console.log('[Debugging] MembershipBlock getMemberProfile', { profileData });
        if (!impersonation.cloak) {
          // Update state and tracking cookie with latest API member data on load
          // console.log('[Debugging Not Cloaked] MembershipBlock getMemberProfile, update Redux and tracking cookie', { profileData, impersonation });
          dispatch(updateTrackingData(profileData));
        };
        return profileData;
      })
      .catch(() => {
        setIsMember(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [impersonation, trackingSelectorData]);

  //  reset success states when fields are altered
  useEffect(() => {
    setValidation({ isValid: undefined, message: '' });
  }, [firstName, lastName, dobDay, dobMonth, dobYear, postcode, gender]);

  useEffect(() => {
    setIsMember(userInformation.id || userInformation.dotDigital.email || trackingSelectorData?.email);
    setMemberEmail(userInformation.email || userInformation.dotDigital.email || trackingSelectorData?.email);
    setFirstName(userInformation.given_names);
    setLastName(userInformation.last_name);
    setDotDigital(userInformation.dotDigital);
    setDOB(dayjs(userInformation.dob));

    setDOBDay(
      userInformation.dob ? dayjs(userInformation.dob).format('D') : dobDay
    );
    setDOBMonth(
      userInformation.dob ? dayjs(userInformation.dob).format('M') : dobMonth
    );
    setDOBYear(
      userInformation.dob ? dayjs(userInformation.dob).format('YYYY') : dobYear
    );
    setPostcode(userInformation.postcode);
    setGender(userInformation.gender || '');
    setUnsubscribeDate(userInformation.unsubscribe_date);

    refreshMemberSubscriptions();
  }, [userInformation]);

  //#region Methods
  const refreshMemberSubscriptions = () => {
    //  merge existing subscriptions into all subscriptions

    mapMemberLevelSubscriptions(userInformation.subscriptions).then(
      globalSubscriptions => {
        //  cache that
        setAllSubscriptions(globalSubscriptions);
        setSubscriptionCheckedState(
          globalSubscriptions.map(sub => sub.isSelected ?? false)
        );
      }
    );
  };

  const calculateUnsubscribeDate = () => {
    //  if unsubscribing and not already unsubscribed, set unsubscribe date to now
    //  else if unsubscribing and already unsubscribed, keep the current unsubscribe date
    //  else set unsubscribe date to null
    return promptUnsubscribe && !userInformation.unsubscribe_date
      ? dayjs().format('YYYY-MM-DD')
      : promptUnsubscribe && unsubscribeDate
      ? dayjs(unsubscribeDate).format('YYYY-MM-DD')
      : null;
  };

  const validate = () => {
    //  validate fields
    if (
      !memberEmail ||
      !firstName ||
      !lastName ||
      !postcode ||
      // !dob ||
      !gender
    ) {
      return { isValid: false, message: 'Please complete all required fields' };
    }

    // if (
    //   !dayjs(
    //     `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`,
    //     'YYYY-MM-DD',
    //     true
    //   ).isValid()
    // ) {
    //   return {
    //     isValid: false,
    //     message: 'Oops. That DOB does not look right. Please try again.',
    //   };
    // }

    return { isValid: true, message: '' };
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const { isValid, message } = validate();
    if (!promptUnsubscribe && !isValid) {
      setValidation({ isValid, message });
      return;
    }

    setIsSaving(true);
    setValidation({ isValid: undefined, message: '' });

    const unSubscribeDate = calculateUnsubscribeDate();

    //  handle empty string because unsubscribe could result in null field values
    const memberData = {
      given_names: firstName == '' ? null : firstName,
      last_name: lastName == '' ? null : lastName,
      postcode: postcode == '' ? null : postcode,
      gender: gender == '' ? null : gender,
      // dob:
      //   dobDay == ''
      //     ? null
      //     : dayjs(
      //         `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(
      //           2,
      //           '0'
      //         )}`
      //       ).format('YYYY-MM-DD'),
      email: userInformation.email,
      unsubscribe_date: unSubscribeDate,
      dotDigital: {
        status: !unSubscribeDate ? 'Subscribed' : dotDigital.status,
      },
      anonymous_uuid: trackingSelectorData.anonymous_uuid || null,
      subscriptions: promptUnsubscribe
        ? []
        : allSubscriptions
            .filter(subscription => {
              return subscription.isSelected == true;
            })
            .map(({ value }) => value),
    };

    return saveMemberProfile(memberData)
    .then((profileData) => {
      // setIsLoading(true);
      if (!promptUnsubscribe) {
        //  do not save changes if unsubscribing - may need to change this
        setUserInformation(prevState => {
          return {
            ...prevState,
            ...profileData[0],
          };
        });
      }
      return profileData;
    })
    .then((profileData) => {
      console.log('[Debugging] MembershipBlock saveMemberProfile', { profileData, impersonation });
      if (!impersonation.cloak) {
        // Update state and tracking cookie with latest API member data on load
        dispatch(updateTrackingData({ ...trackingSelectorData, ...profileData }));
        console.log('[Debugging Not Cloaked] MembershipBlock saveMemberProfile updated tracking cookie', { ...trackingSelectorData, ...profileData });
      };
    })
    .then(() => {
      refreshMemberSubscriptions();
      if (memberData.unsubscribe_date && promptUnsubscribe) {
        setPromptUnsubscribeSuccessful(true);
      }
      setPromptUnsubscribe(false);
      setPromptConfirmEmail('');
    })
    .then(() => {
      setRedirectData({ email: memberData.email!, cloak: impersonation.cloak });
    })
    .catch((e) => {
      setPromptUnsubscribe(false);
      setPromptConfirmEmail('');
      setValidation({
        isValid: false,
        message: 'Oops, there was a problem saving. Please try again later.',
      })
      setIsSaving(false);
      // setIsLoading(false);
      console.error({error: e.message, email: userInformation.email}, 'saveMemberProfile');
    });
  };

  const handleSubscriptionChange = position => {
    const updatedSubscriptions = allSubscriptions.map((item, index) => {
      return index === position
        ? { ...item, isSelected: !item.isSelected }
        : item;
    });
    setAllSubscriptions(updatedSubscriptions);
    setPromptUnsubscribe(false);

    const updatedCheckedState = subscriptionCheckedState.map((item, index) => {
      return index === position ? !item : item;
    });
    setSubscriptionCheckedState(updatedCheckedState);
  };
  //#endregion Methods

  return (
    <>
      <div style={{ minHeight: '70vh', alignItems: 'center', justifyContent: 'center', display: isLoading ? 'flex' : 'none' }}>
        <Spinner>
          <p>Loading...</p>
        </Spinner>
      </div>

      {!isLoading && !redirectData && (
        <>
          {!isMember && 
            <MembershipSignupPanel {...block}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
              validation={validation}
              setValidation={setValidation}
              setIsLoading={setIsLoading}
              onSignUp={(subscriberDetails: any) => {
                console.log('[Debugging] MembershipBlock onSignUp', { subscriberDetails });
                dispatch(updateTrackingData({ ...trackingSelectorData, ...subscriberDetails }));
                setIsSaving(true);
                setIsLoading(true);
                setImpersonation({ email: subscriberDetails.email, cloak: impersonation.cloak });
                setIsMember(!!subscriberDetails.email);
                // setRedirectData({ email, cloak: impersonation.cloak });
              }}
            />}

          {isMember && (
            <>
              <MembershipPanel {...{ ...block, userInformation, isCloaked: impersonation.cloak }} 
                onSignOut={() => {
                  signOut().then(() => {
                    setIsLoading(true);
                    setIsMember(false);
                  }).then(() => {
                    console.log('[Debugging] MembershipBlock signOut redirecting to /membercentre');
                    window.location.href = '/membercentre';
                  }).catch((e) => {
                    console.error('[MembershipBlock] Error during signOut', e);
                  });
                }}
              />
              
              <form
                onSubmit={handleProfileSubmit}
                id="profile"
                className={clsx(`needs-validation ${styles.profileContainer}`)}
                noValidate
                autoComplete="off"
              >
                <div className={`membership-form-container ${styles.section}`}>
                  <Spacer width={1160} space={'m'}>
                    <Typography>
                      <h2>{block.profileTitle}</h2>
                      {block.profileDescription && (
                        <p>{block.profileDescription}</p>
                      )}
                      <div
                        className={`formGroupContainer ${styles.formGroupContainer}`}
                      >
                        <div className={`formGroup ${styles.formGroup}`}>
                          <label
                            className="control-label"
                            htmlFor="profile-field-firstname"
                          >
                            First Name <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !firstName &&
                                !validation.isValid &&
                                !promptUnsubscribeSuccessful,
                            })}
                            id={`profile-field-firstname`}
                            type="text"
                            required
                            value={firstName}
                            autoFocus={validation.isValid != false}
                            onChange={e => setFirstName(e.target.value)}
                          />
                        </div>
                        <div className={`formGroup ${styles.formGroup}`}>
                          <label
                            className="control-label"
                            htmlFor="profile-field-lastname"
                          >
                            Last Name <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !lastName &&
                                !validation.isValid &&
                                !promptUnsubscribeSuccessful,
                            })}
                            id={`profile-field-lastname`}
                            type="text"
                            required
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                          />
                        </div>
                        <div
                          className={`formGroup ${styles.formGroup} ${styles.genderGroup}`}
                        >
                          <label
                            className="control-label"
                            htmlFor="profile-field-gender"
                          >
                            Gender <span style={{ color: 'red' }}>*</span>
                          </label>
                          <select
                            id={`profile-field-gender`}
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !gender &&
                                !validation.isValid &&
                                !promptUnsubscribeSuccessful,
                            })}
                            value={gender}
                            required
                            onChange={e => setGender(e.target.value)}
                          >
                            <option value="">Select your gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="N/A">Prefer not to say</option>
                          </select>
                        </div>
                        <div
                          className={`formGroup ${styles.formGroup} ${styles.postcodeGroup}`}
                        >
                          <label
                            className="control-label"
                            htmlFor="profile-field-postcode"
                          >
                            Postcode <span style={{ color: 'red' }}>*</span>
                          </label>
                          <input
                            className={clsx(`form-control`, {
                              'is-invalid':
                                !postcode &&
                                !validation.isValid &&
                                !promptUnsubscribeSuccessful,
                            })}
                            id={`profile-field-postcode`}
                            type="text"
                            required
                            value={postcode}
                            onChange={e => setPostcode(e.target.value)}
                          />
                        </div>
                        {/* <div
                          className={`formGroup ${styles.formGroup} ${styles.dobGroup}`}
                        >
                          <label
                            className="control-label"
                            htmlFor="profile-field-dob"
                          >
                            Date of Birth{' '}
                            <span style={{ color: 'red' }}>*</span>
                          </label>
                          <Spacer
                            row
                            selfH={0}
                            width={`100%`}
                            className={`${styles.dobContainer}`}
                          >
                            <select
                              className={clsx(`form-control pr-2`, {
                                'is-invalid':
                                  !dobDay &&
                                  !validation.isValid &&
                                  !promptUnsubscribeSuccessful,
                              })}
                              value={dobDay}
                              onChange={e => setDOBDay(e.target.value)}
                            >
                              {Array.from({ length: 31 }, (_, i) => i + 1).map(
                                key => (
                                  <option key={key} value={key}>
                                    {key}
                                  </option>
                                )
                              )}
                            </select>
                            <select
                              className={clsx(`form-control pr-2`, {
                                'is-invalid':
                                  !dobMonth &&
                                  !validation.isValid &&
                                  !promptUnsubscribeSuccessful,
                              })}
                              value={dobMonth}
                              required
                              onChange={e => setDOBMonth(e.target.value)}
                            >
                              <option value="">Month</option>
                              {months.map((month, key) => (
                                <option key={key} value={month.index}>
                                  {month.full}
                                </option>
                              ))}
                            </select>
                            <select
                              className={clsx(`form-control pr-2`, {
                                'is-invalid':
                                  !dobYear &&
                                  !validation.isValid &&
                                  !promptUnsubscribeSuccessful,
                              })}
                              value={dobYear}
                              onChange={e => setDOBYear(e.target.value)}
                            >
                              {constructNumberRange(
                                1930,
                                parseInt(
                                  dayjs()
                                    .add(dayjs.duration({ years: -15 }))
                                    .format('YYYY')
                                )
                              )
                                .reverse()
                                .map((year, key) => (
                                  <option key={key} value={year}>
                                    {year}
                                  </option>
                                ))}
                            </select>
                          </Spacer>
                        </div> */}
                      </div>
                    </Typography>
                    <div
                      className={`membership-form-controls-container justify-content-start
                        ${styles.controlsContainer} ${styles.section}`}
                    >
                      <Spacer
                        row
                        width={1160}
                        space={'m'}
                        align={'center'}
                        justify={'flex-start'}
                      >
                        {isSaving && (
                          <Button disabled={true} onClick={() => {}}>
                            <Icon
                              icon={'autorenew'}
                              rotateIcon={true}
                              className={'mr-2'}
                            />
                            <span>Please wait...</span>
                          </Button>
                        )}
                        {!isSaving && (
                          <Button type={'submit'} 
                            onClick={() => raiseEvent(MEMBERCENTRE_UPDATE_CLICK)}
                          >
                            <span>
                              {dotDigital && dotDigital.status != 'Subscribed'
                                ? block.resubscribeButtonText
                                : block.saveButtonText}
                            </span>
                          </Button>
                        )}
                      </Spacer>
                      <Spacer
                        row
                        width={1160}
                        space={'m'}
                        align={'center'}
                        justify={'center'}
                      >
                        {validation.isValid === true && (
                          <div
                            className={`alert alert-warning text-center ${styles.alert}`}
                            role="alert"
                          >
                            {validation.message}
                          </div>
                        )}
                        {validation.isValid === false && (
                          <div
                            className={`alert alert-danger text-center ${styles.alert}`}
                            role="alert"
                          >
                            {validation.message}
                          </div>
                        )}
                      </Spacer>
                    </div>
                  </Spacer>
                </div>
                <div
                  id="subscriptions"
                  className={`membership-subscriptions-container ${styles.section} ${styles.noBackground}`}
                >
                  <Spacer width={1160} space={'m'}>
                    <Typography>
                      <h2>{block.subscriptionsTitle}</h2>
                      {block.subscriptionsDescription && (
                        <p>{block.subscriptionsDescription}</p>
                      )}
                      <Spacer
                        row
                        className={`subscriptions ${styles.subscriptions}`}
                      >
                        {allSubscriptions.map(
                          (sub: MemberLevelSubscription, key) => (
                            <label
                              htmlFor={`subscription-checkbox-${key}`}
                              key={key}
                              className={`subscriptionItem ${styles.subscriptionItem}`}
                            >
                              <img src={sub.image} alt={sub.value} />
                              <h4>{sub.label}</h4>
                              <p>{sub.description}</p>
                              <input
                                id={`subscription-checkbox-${key}`}
                                name={sub.value}
                                type="checkbox"
                                value={sub.value}
                                checked={subscriptionCheckedState[key]}
                                onChange={() => handleSubscriptionChange(key)}
                              />
                              <Icon
                                size={'xxxl'}
                                className={
                                  subscriptionCheckedState[key]
                                    ? 'check_circle'
                                    : 'highlight_off'
                                }
                                icon={
                                  subscriptionCheckedState[key]
                                    ? 'check_circle'
                                    : 'highlight_off'
                                }
                              />
                            </label>
                          )
                        )}
                      </Spacer>
                    </Typography>

                    <div
                      className={`membership-form-controls-container ${styles.controlsContainer} ${styles.section} ${styles.noBackground}`}
                    >
                      <Spacer
                        row
                        width={1160}
                        space={['m', 'l']}
                        align={'center'}
                        justify={'center'}
                      >
                        {isSaving && (
                          <>
                            <Button disabled={true} onClick={() => {}}>
                              <Icon
                                icon={'autorenew'}
                                rotateIcon={true}
                                className={'mr-2'}
                              />
                              <span>Please wait...</span>
                            </Button>
                            <Button disabled={true} onClick={() => {}}>
                              <Icon
                                icon={'autorenew'}
                                rotateIcon={true}
                                className={'mr-2'}
                              />
                              <span>Please wait...</span>
                            </Button>
                          </>
                        )}
                        {!isSaving && (
                          <>
                            <Button
                              type={'submit'}
                              onClick={() => {
                                setValidation({ isValid: undefined, message: '' });
                                setPromptUnsubscribeSuccessful(undefined);
                                setPromptUnsubscribe(false);
                                setPromptConfirmEmail('');
                                setUnsubscribeDate(Date.now());
                                raiseEvent(MEMBERCENTRE_UPDATE_CLICK);
                                // console.log('Save Membership');
                              }}
                            >
                              <span>
                                {dotDigital && dotDigital.status != 'Subscribed'
                                  ? block.resubscribeButtonText
                                  : block.saveButtonText}
                              </span>
                            </Button>
                            {dotDigital && dotDigital.status == 'Subscribed' && (
                              <Button
                                type="button"
                                onClick={() => {
                                  setPromptUnsubscribe(true);
                                  setPromptUnsubscribeSuccessful(undefined);
                                  setValidation({ isValid: undefined, message: '' });
                                  // console.log('Unsubscribe All Prompt');
                                }}
                              >
                                <span>{block.unsubscribeButtonText}</span>
                              </Button>
                            )}
                          </>
                        )}
                      </Spacer>
                      <Spacer
                        row
                        width={1160}
                        space={'m'}
                        align={'center'}
                        justify={'center'}
                      >
                        {validation.isValid === true && (
                          <div
                            className={`alert alert-warning text-center ${styles.alert}`}
                            role="alert"
                          >
                            {validation.message}
                          </div>
                        )}
                        {validation.isValid === false && (
                          <div
                            className={`alert alert-danger text-center ${styles.alert}`}
                            role="alert"
                          >
                            {validation.message}
                          </div>
                        )}

                        {promptUnsubscribe && (
                          <div
                            className={`alert alert-warning ${styles.alert}`}
                            role="alert"
                          >
                            <Spacer
                              row={false}
                              space={'m'}
                              align={'center'}
                              justify={'center'}
                            >
                              <p>{block.unsubscribePromptText}</p>

                              <div className="mt-0">
                                <label
                                  className="control-label text-left px-0"
                                  htmlFor="unsubscribe-email-confirm"
                                >
                                  Confirm your email address and proceed{' '}
                                  <span style={{ color: 'red' }}>*</span>
                                </label>
                                <input
                                  className={`form-control`}
                                  id={`unsubscribe-email-confirm`}
                                  type="email"
                                  required
                                  autoFocus={promptUnsubscribe}
                                  value={promptConfirmEmail}
                                  onChange={e =>
                                    setPromptConfirmEmail(e.target.value)
                                  }
                                />
                              </div>

                              <Spacer
                                row
                                space={'m'}
                                align={'center'}
                                justify={'center'}
                                className={`mt-2`}
                              >
                                <Button
                                  type={'submit'}
                                  disabled={promptConfirmEmail !== memberEmail}
                                  onClick={() => {
                                    setUnsubscribeDate(Date.now());
                                    raiseEvent(MEMBERCENTRE_UNSUBSCRIBE_CLICK);
                                    // console.log('Do Unsubscribe All');
                                  }}
                                >
                                  <span>{block.unsubscribeButtonText}</span>
                                </Button>
                                <Button
                                  className={`ml-2`}
                                  type="button"
                                  onClick={() => {
                                    setPromptUnsubscribe(false);
                                    setPromptUnsubscribeSuccessful(undefined);
                                    setPromptConfirmEmail('');
                                    // console.log('Cancel Unsubscribe Prompt');
                                  }}
                                >
                                  <span>Cancel</span>
                                </Button>
                              </Spacer>
                            </Spacer>
                          </div>
                        )}

                        {promptUnsubscribeSuccessful && (
                          <div
                            className={`alert alert-warning text-center ${styles.alert}`}
                            role="alert"
                          >
                            <p>{block.unsubscribeConfirmationText}</p>
                          </div>
                        )}
                      </Spacer>
                    </div>
                  </Spacer>
                </div>
              </form>
            </>
          )}
        </>
      )}
    </>
  );
};

export default compose(styled(membershipBlockStyles))(MembershipBlock);
